import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Upload, DollarSign, Tag, Image as ImageIcon, ZoomIn, ZoomOut, Check, GalleryHorizontal, Copy, ExternalLink, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { databaseService, storageService, ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';
import FeaturedButton from '../../../components/FeaturedButton/FeaturedButton';

const SHOP_COLLECTION = 'products';
const SHOP_BUCKET = 'shop-images';

// Image Editor Component with Slider Zoom
function ImageEditor({ imageFile, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(0.1);
  const [maxScale, setMaxScale] = useState(3);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  // Card preview dimensions (matching ProductCard)
  const previewWidth = 260;
  const previewHeight = 200;

  useEffect(() => {
    if (imageFile) {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        
        // Calculate scale to cover the preview area (cover, not contain)
        const scaleX = previewWidth / img.width;
        const scaleY = previewHeight / img.height;
        const coverScale = Math.max(scaleX, scaleY);
        
        // Set min scale to cover the preview, max to 3x of cover
        const minS = coverScale;
        const maxS = coverScale * 4;
        setMinScale(minS);
        setMaxScale(maxS);
        
        // Start at cover scale (image fills the preview)
        const initialScale = coverScale;
        setScale(initialScale);
        
        // Center the image
        setPosition({
          x: (previewWidth - img.width * initialScale) / 2,
          y: (previewHeight - img.height * initialScale) / 2
        });
      };
      img.src = URL.createObjectURL(imageFile);
      return () => URL.revokeObjectURL(img.src);
    }
  }, [imageFile]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleTouchMove = useCallback((e) => {
    if (isDragging) {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove]);

  const handleSliderChange = (e) => {
    const newScale = parseFloat(e.target.value);
    
    // Adjust position to zoom from center
    if (image) {
      const centerX = previewWidth / 2;
      const centerY = previewHeight / 2;
      const imgCenterX = position.x + (image.width * scale) / 2;
      const imgCenterY = position.y + (image.height * scale) / 2;
      
      const newImgWidth = image.width * newScale;
      const newImgHeight = image.height * newScale;
      
      setPosition({
        x: centerX - (centerX - position.x) * (newScale / scale),
        y: centerY - (centerY - position.y) * (newScale / scale)
      });
    }
    
    setScale(newScale);
  };

  const handleSave = async () => {
    if (!image || !canvasRef.current) return;
    setSaving(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Higher resolution output
    const outputScale = 2;
    canvas.width = previewWidth * outputScale;
    canvas.height = previewHeight * outputScale;
    ctx.scale(outputScale, outputScale);
    
    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, previewWidth, previewHeight);
    
    // Draw the image with current position and scale
    ctx.drawImage(
      image,
      position.x,
      position.y,
      image.width * scale,
      image.height * scale
    );

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'product-image.jpg', { type: 'image/jpeg' });
        await onSave(file);
      }
      setSaving(false);
    }, 'image/jpeg', 0.92);
  };

  // Calculate zoom percentage relative to cover scale
  const zoomPercent = image ? Math.round((scale / minScale) * 100) : 100;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Adjust Image</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-500 mb-3 text-center">
            Drag to position • Use slider to zoom
          </p>

          {/* Preview Area */}
          <div 
            className="relative mx-auto bg-gray-100 rounded-xl overflow-hidden cursor-move border-2 border-[#4C1D95] shadow-lg"
            style={{ width: previewWidth, height: previewHeight }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {image && (
              <img
                src={image.src}
                alt="Preview"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: position.x,
                  top: position.y,
                  width: image.width * scale,
                  height: image.height * scale,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
              />
            )}
            {/* Center crosshair */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white/50 rounded-full"></div>
            </div>
          </div>

          {/* Hidden canvas for export */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Zoom Slider */}
          <div className="mt-5 px-2">
            <div className="flex items-center gap-3">
              <ZoomOut className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input
                type="range"
                min={minScale}
                max={maxScale}
                step={(maxScale - minScale) / 100}
                value={scale}
                onChange={handleSliderChange}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#4C1D95]"
                style={{
                  background: `linear-gradient(to right, #4C1D95 0%, #4C1D95 ${((scale - minScale) / (maxScale - minScale)) * 100}%, #e5e7eb ${((scale - minScale) / (maxScale - minScale)) * 100}%, #e5e7eb 100%)`
                }}
              />
              <ZoomIn className="w-5 h-5 text-gray-400 flex-shrink-0" />
            </div>
            <p className="text-center text-sm text-[#4C1D95] font-medium mt-2">{zoomPercent}%</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-5">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#4C1D95] text-white rounded-lg hover:bg-[#0d4543] flex items-center justify-center gap-2 font-medium"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Apply
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


const CATEGORY_COLLECTION = 'shop_categories';

export default function ProductManagement() {
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [tagInput, setTagInput] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    discountedPrice: '',
    onSale: false,
    featured: false,
    imageUrl: '',
    imageId: '',
    fullImageUrl: '',
    fullImageId: '',
    category: '',
    tags: [],
    description: '',
    additionalInfo: '',
    galleryUrls: [],
    galleryIds: [],
    customSlug: '',
    useProductPrefix: true,
  });
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const result = await databaseService.listDocuments(CATEGORY_COLLECTION);
      if (result.success) {
        setCategories(result.data.documents.sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const result = await databaseService.listDocuments(SHOP_COLLECTION);
      if (result.success) {
        setProducts(result.data.documents);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      showToast('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('Image size should be less than 10MB', 'error');
      return;
    }

    setSelectedImageFile(file);
    setShowImageEditor(true);
  };

  const handleImageSave = async (croppedFile) => {
    setShowImageEditor(false);
    setUploading(true);

    try {
      // Delete old images if exist
      if (formData.imageId) {
        try { await storageService.deleteFile(SHOP_BUCKET, formData.imageId); } catch (e) {}
      }
      if (formData.fullImageId) {
        try { await storageService.deleteFile(SHOP_BUCKET, formData.fullImageId); } catch (e) {}
      }

      // Upload original full image first
      const fullFileId = ID.unique();
      const fullResult = await storageService.uploadFile(SHOP_BUCKET, fullFileId, selectedImageFile);
      
      // Upload cropped thumbnail
      const thumbFileId = ID.unique();
      const thumbResult = await storageService.uploadFile(SHOP_BUCKET, thumbFileId, croppedFile);
      
      if (fullResult.success && thumbResult.success) {
        const fullImageId = fullResult.data.$id;
        const fullImageUrl = storageService.getFileView(SHOP_BUCKET, fullImageId);
        const imageId = thumbResult.data.$id;
        const imageUrl = storageService.getFileView(SHOP_BUCKET, imageId);
        
        setFormData(prev => ({
          ...prev,
          imageUrl,
          imageId,
          fullImageUrl,
          fullImageId,
        }));
        showToast('Image uploaded successfully', 'success');
      } else {
        showToast('Failed to upload image', 'error');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploading(false);
      setSelectedImageFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Product name is required', 'error');
      return;
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      showToast('Valid price is required', 'error');
      return;
    }

    const productData = {
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      discountedPrice: formData.discountedPrice ? parseFloat(formData.discountedPrice) : null,
      onSale: formData.onSale,
      featured: formData.featured,
      imageUrl: formData.imageUrl,
      imageId: formData.imageId,
      fullImageUrl: formData.fullImageUrl,
      fullImageId: formData.fullImageId,
      category: formData.category,
      tags: formData.tags,
      description: formData.description,
      additionalInfo: formData.additionalInfo,
      galleryUrls: formData.galleryUrls,
      galleryIds: formData.galleryIds,
      customSlug: formData.customSlug,
      useProductPrefix: formData.useProductPrefix,
    };

    try {
      if (editingProduct) {
        const result = await databaseService.updateDocument(
          SHOP_COLLECTION, editingProduct.$id, productData
        );
        if (result.success) {
          showToast('Product updated successfully', 'success');
        } else {
          showToast('Failed to update product', 'error');
        }
      } else {
        const result = await databaseService.createDocument(
          SHOP_COLLECTION, productData
        );
        if (result.success) {
          showToast('Product created successfully', 'success');
        } else {
          showToast('Failed to create product', 'error');
        }
      }
      
      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      showToast('Failed to save product', 'error');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      price: product.price?.toString() || '',
      discountedPrice: product.discountedPrice?.toString() || '',
      onSale: product.onSale || false,
      featured: product.featured || false,
      imageUrl: product.imageUrl || '',
      imageId: product.imageId || '',
      fullImageUrl: product.fullImageUrl || '',
      fullImageId: product.fullImageId || '',
      category: product.category || '',
      tags: product.tags || [],
      description: product.description || '',
      additionalInfo: product.additionalInfo || '',
      galleryUrls: product.galleryUrls || [],
      galleryIds: product.galleryIds || [],
      customSlug: product.customSlug || '',
      useProductPrefix: product.useProductPrefix !== undefined ? product.useProductPrefix : true,
    });
    setTagInput('');
    setShowModal(true);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;

    try {
      // Delete both images
      if (product.imageId) {
        try { await storageService.deleteFile(SHOP_BUCKET, product.imageId); } catch (e) {}
      }
      if (product.fullImageId) {
        try { await storageService.deleteFile(SHOP_BUCKET, product.fullImageId); } catch (e) {}
      }
      
      const result = await databaseService.deleteDocument(
        SHOP_COLLECTION, product.$id
      );
      
      if (result.success) {
        showToast('Product deleted successfully', 'success');
        loadProducts();
      } else {
        showToast('Failed to delete product', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('Failed to delete product', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', price: '', discountedPrice: '', onSale: false, featured: false, 
      imageUrl: '', imageId: '', fullImageUrl: '', fullImageId: '',
      category: '', tags: [], description: '', additionalInfo: '',
      galleryUrls: [], galleryIds: [], customSlug: '', useProductPrefix: true,
    });
    setTagInput('');
    setEditingProduct(null);
  };

  // Gallery image upload handler
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingGallery(true);
    const newUrls = [...formData.galleryUrls];
    const newIds = [...formData.galleryIds];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) continue;

        const fileId = ID.unique();
        const result = await storageService.uploadFile(SHOP_BUCKET, fileId, file);
        
        if (result.success) {
          const imageUrl = storageService.getFileView(SHOP_BUCKET, result.data.$id);
          newUrls.push(imageUrl);
          newIds.push(result.data.$id);
        }
      }

      setFormData(prev => ({
        ...prev,
        galleryUrls: newUrls,
        galleryIds: newIds,
      }));
      showToast(`${files.length} image(s) uploaded`, 'success');
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      showToast('Failed to upload some images', 'error');
    } finally {
      setUploadingGallery(false);
    }
  };

  // Remove gallery image
  const removeGalleryImage = async (index) => {
    const imageId = formData.galleryIds[index];
    
    try {
      if (imageId) {
        await storageService.deleteFile(SHOP_BUCKET, imageId);
      }
    } catch (e) {
      console.error('Error deleting gallery image:', e);
    }

    setFormData(prev => ({
      ...prev,
      galleryUrls: prev.galleryUrls.filter((_, i) => i !== index),
      galleryIds: prev.galleryIds.filter((_, i) => i !== index),
    }));
  };

  const addTag = () => {
    const input = tagInput.trim();
    if (!input) return;

    // Check if input contains commas (bulk input)
    if (input.includes(',')) {
      const newTags = input
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !formData.tags.includes(tag));
      
      if (newTags.length > 0) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, ...newTags] }));
        showToast(`${newTags.length} tag(s) added successfully`, 'success');
      }
    } else {
      // Single tag input
      if (!formData.tags.includes(input)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, input] }));
      }
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Product Management</h1>
          <p className="text-gray-600 mt-1">Manage, organize, and track your shop inventory</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 px-5 py-2.5 bg-[#4C1D95] text-white rounded-lg hover:bg-[#0d4543] transition-all shadow-md hover:shadow-lg active:scale-95 font-medium">
          <Plus className="w-5 h-5" />
          Add Product
        </button>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4C1D95]"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ImageIcon className="w-10 h-10 text-[#4C1D95]" />
          </div>
          <p className="text-gray-500 mb-6">No products yet. Add your first product to start selling!</p>
          <button onClick={openAddModal} className="inline-flex items-center gap-2 px-6 py-2 bg-[#4C1D95] text-white rounded-lg hover:bg-[#0d4543] transition-colors">
             <Plus className="w-4 h-4" /> Create Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <div key={product.$id} className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
              <div className="relative h-56 bg-gray-100 overflow-hidden">
                {product.imageUrl ? (
                  <>
                    <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50">
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                  {product.onSale && (
                     <span className="px-2.5 py-1 bg-red-500/90 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wide rounded-full shadow-sm">SALE</span>
                  )}
                  {product.featured && (
                     <FeaturedButton featured={product.featured} />
                  )}
                </div>
              </div>
              
              <div className="p-5 flex-1 flex flex-col">
                <div className="mb-2">
                   <h3 className="font-bold text-gray-800 text-lg line-clamp-1 group-hover:text-[#4C1D95] transition-colors">{product.name}</h3>
                   <span className="text-xs text-gray-500 font-medium">{product.category || 'Uncategorized'}</span>
                </div>

                <div className="flex items-center gap-2 mb-6">
                  {product.onSale && product.discountedPrice ? (
                    <>
                      <span className="text-xl font-bold text-[#4C1D95]">${product.discountedPrice?.toFixed(2)}</span>
                      <span className="text-gray-400 line-through text-sm font-medium decoration-2">${product.price?.toFixed(2)}</span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-[#4C1D95]">${product.price?.toFixed(2)}</span>
                  )}
                </div>

                <div className="mt-auto grid grid-cols-4 gap-2">
                   <button 
                      onClick={() => handleEdit(product)} 
                      className="col-span-2 flex items-center justify-center gap-2 py-2 bg-gray-50 hover:bg-[#4C1D95] text-gray-700 hover:text-white rounded-lg transition-all font-medium text-sm group/btn"
                   >
                     <Edit2 className="w-4 h-4 group-hover/btn:scale-110 transition-transform" /> Edit
                   </button>
                   
                   <Link
                    to={`/admin/analytics/products/${product.$id}`}
                    className="flex items-center justify-center py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    title="Analytics"
                   >
                    <ExternalLink className="w-4 h-4" />
                   </Link>

                   <button 
                    onClick={() => handleDelete(product)} 
                    className="flex items-center justify-center py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    title="Delete"
                   >
                     <Trash2 className="w-4 h-4" />
                   </button>
                </div>
                 
                 {/* Copy ID Button Overlay */}
                 {product.customSlug && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                       <span className="text-[10px] text-gray-400 font-mono truncate max-w-[150px]">/{product.customSlug}</span>
                       <button
                         onClick={() => {
                            const url = `${window.location.origin}${product.useProductPrefix ? '/product/' : '/'}${product.customSlug}`;
                            navigator.clipboard.writeText(url);
                            showToast('Link copied', 'success');
                         }}
                         className="text-[10px] uppercase font-bold text-[#4C1D95] hover:underline"
                       >
                         Copy Link
                       </button>
                    </div>
                 )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6 md:p-10 overflow-hidden">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-full flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 ring-1 ring-black/5">
            {/* Header - Sticky */}
            <div className="flex items-center justify-between p-5 border-b bg-gray-50/80 backdrop-blur-md sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                <p className="text-[11px] text-[#4C1D95] font-bold uppercase tracking-widest mt-0.5 opacity-70">Commerce Dashboard</p>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 hover:bg-gray-200 rounded-full transition-all text-gray-400 hover:text-gray-600 hover:rotate-90"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              {/* Scrollable Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-8 scroll-smooth custom-scrollbar">
                
                {/* Section 1: Basic Info & Image */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column: Image Upload */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Product Visuals</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 bg-gray-50/30 hover:bg-gray-50 transition-colors">
                      {formData.imageUrl ? (
                        <div className="relative group">
                          <img src={formData.imageUrl} alt="Product" className="w-full h-64 object-cover rounded-xl shadow-md" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                            <label className="p-2 bg-white text-gray-800 rounded-full cursor-pointer hover:bg-gray-100 shadow-lg">
                              <Edit2 className="w-5 h-5" />
                              <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                            </label>
                            <button 
                              type="button" 
                              onClick={() => setFormData(prev => ({ ...prev, imageUrl: '', imageId: '', fullImageUrl: '', fullImageId: '' }))} 
                              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer min-h-[256px]">
                          {uploading ? (
                            <div className="flex flex-col items-center gap-3">
                              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#4C1D95] border-t-transparent"></div>
                              <span className="text-sm font-medium text-gray-500">Processing Image...</span>
                            </div>
                          ) : (
                            <div className="text-center group">
                              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                                <Upload className="w-8 h-8 text-[#4C1D95]" />
                              </div>
                              <span className="text-base font-semibold text-gray-700 block">Upload Main Image</span>
                              <span className="text-sm text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                            </div>
                          )}
                          <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" disabled={uploading} />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Key Details */}
                  <div className="space-y-6">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Product Details</label>
                    <div className="grid grid-cols-1 gap-5">
                      {/* Name */}
                      <div className="col-span-full">
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Product Name</label>
                        <input 
                          type="text" 
                          value={formData.name} 
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} 
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] transition-all" 
                          placeholder="e.g. Premium Map Print" 
                          required 
                        />
                      </div>

                      {/* Category */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] transition-all"
                        >
                          <option value="">Uncategorized</option>
                          {categories.map(cat => (
                            <option key={cat.$id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Price */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Price ($)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input 
                              type="number" 
                              step="0.01" 
                              min="0" 
                              value={formData.price} 
                              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} 
                              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] transition-all" 
                              placeholder="0.00" 
                              required 
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Sale Price ($)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input 
                              type="number" 
                              step="0.01" 
                              min="0" 
                              value={formData.discountedPrice} 
                              onChange={(e) => setFormData(prev => ({ ...prev, discountedPrice: e.target.value }))} 
                              className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] transition-all ${!formData.onSale && 'opacity-50 cursor-not-allowed'}`} 
                              placeholder="0.00" 
                              disabled={!formData.onSale}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Toggles */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">On Sale</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={formData.onSale} onChange={(e) => setFormData(prev => ({ ...prev, onSale: e.target.checked }))} className="sr-only peer" />
                            <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Featured</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))} className="sr-only peer" />
                            <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#4C1D95]"></div>
                          </label>
                        </div>
                      </div>

                      {/* Link Management Section */}
                      <div className="space-y-4 pt-4 border-t border-gray-100">
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Product Link</label>
                        
                        {/* Custom Slug */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Custom URL Slug</label>
                          <input
                            type="text"
                            value={formData.customSlug}
                            onChange={(e) => {
                              const value = e.target.value.toLowerCase()
                                .replace(/[^a-z0-9-]+/g, '-')
                                .replace(/^-+|-+$/g, '');
                              setFormData(prev => ({ ...prev, customSlug: value }));
                            }}
                            placeholder="my-awesome-product"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] transition-all text-sm"
                          />
                        </div>

                        {/* Auto-generate button */}
                        <button
                          type="button"
                          onClick={() => {
                            const slug = formData.name.toLowerCase()
                              .replace(/[^a-z0-9-]+/g, '-')
                              .replace(/^-+|-+$/g, '');
                            setFormData(prev => ({ ...prev, customSlug: slug }));
                            showToast('Slug generated from product name', 'success');
                          }}
                          className="text-xs text-[#4C1D95] hover:text-[#0d4543] font-medium"
                        >
                          Auto-generate from product name
                        </button>

                        {/* Use Product Prefix Toggle */}
                        <div className="flex items-center justify-between p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                          <div>
                            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide block">Use /product/ prefix</span>
                            <span className="text-xs text-gray-500">Enable for /product/slug URLs</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={formData.useProductPrefix} 
                              onChange={(e) => setFormData(prev => ({ ...prev, useProductPrefix: e.target.checked }))} 
                              className="sr-only peer" 
                            />
                            <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#4C1D95]"></div>
                          </label>
                        </div>

                        {/* Preview URL with Copy Button */}
                        {formData.customSlug && (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <span className="text-xs font-bold text-green-700 uppercase tracking-wide">Preview URL</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const url = `${window.location.origin}${formData.useProductPrefix ? '/product/' : '/'}${formData.customSlug}`;
                                  navigator.clipboard.writeText(url);
                                  showToast('URL copied to clipboard!', 'success');
                                }}
                                className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                              >
                                <Copy className="w-3 h-3" />
                                Copy
                              </button>
                            </div>
                            <code className="text-sm text-green-700 break-all block mb-2">
                              {window.location.origin}{formData.useProductPrefix ? '/product/' : '/'}{formData.customSlug}
                            </code>
                            <p className="text-xs text-green-600">
                              This will be the public URL for your product page
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-gray-100" />

                {/* Section 2: Tags & Gallery */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Tags */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Discovery Tags</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 transition-all font-medium text-sm"
                        placeholder="Add keywords... (separate multiple tags with commas)"
                      />
                      <button type="button" onClick={addTag} className="px-4 py-2 bg-gray-100 text-[#4C1D95] font-bold rounded-xl hover:bg-gray-200 transition-colors shadow-sm">Add</button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Separate multiple tags with commas for bulk input
                    </p>
                    <div className="flex flex-wrap gap-2 min-h-10">
                      {formData.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#4C1D95]/5 text-[#4C1D95] text-xs font-bold rounded-full border border-[#4C1D95]/10 group transition-all hover:bg-[#4C1D95]/10 shadow-sm">
                          {tag}
                          <button type="button" onClick={() => removeTag(tag)} className="p-0.5 hover:bg-red-500 hover:text-white rounded-full transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {formData.tags.length === 0 && <span className="text-gray-400 text-xs italic py-2">No tags added yet</span>}
                    </div>
                  </div>

                  {/* Gallery */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Gallery Collection</label>
                    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 min-h-[120px]">
                      <div className="flex flex-wrap gap-3 mb-4">
                        {formData.galleryUrls.map((url, idx) => (
                          <div key={idx} className="relative group">
                            <img src={url} alt={`Gallery ${idx + 1}`} className="w-20 h-20 object-cover rounded-xl border border-white shadow-sm ring-1 ring-gray-200/50" />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(idx)}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all hover:scale-110 shadow-lg"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <label className="w-20 h-20 flex flex-col items-center justify-center cursor-pointer bg-white border-2 border-dashed border-gray-200 rounded-xl hover:border-[#4C1D95]/30 transition-all hover:bg-white/80 group shadow-sm">
                          {uploadingGallery ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#4C1D95] border-t-transparent" />
                          ) : (
                            <>
                              <Plus className="w-6 h-6 text-gray-400 group-hover:text-[#4C1D95] transition-colors" />
                            </>
                          )}
                          <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" disabled={uploadingGallery} />
                        </label>
                      </div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide font-bold">Max 10MB per image • Square aspect recommended</p>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-gray-100" />

                {/* Section 3: Rich Content */}
                <div className="grid grid-cols-1 gap-8">
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Product Story (Description)</label>
                    <div className="rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-[#4C1D95]/20 transition-all shadow-sm">
                      <RichTextEditor
                        value={formData.description}
                        onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                        placeholder="Share the story and features of this product..."
                        minHeight="160px"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Additional Specs & Info</label>
                    <div className="rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-[#4C1D95]/20 transition-all shadow-sm">
                      <RichTextEditor
                        value={formData.additionalInfo}
                        onChange={(value) => setFormData(prev => ({ ...prev, additionalInfo: value }))}
                        placeholder="Materials, dimensions, care instructions, etc..."
                        minHeight="160px"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer - Sticky */}
              <div className="p-5 border-t bg-gray-50 flex gap-3 justify-end items-center px-6 shadow-[0_-10px_30px_rgba(0,0,0,0.02)]">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors uppercase tracking-widest"
                >
                  Discard
                </button>
                <button 
                  type="submit" 
                  className="px-8 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#0d4543] transition-all hover:scale-[1.02] active:scale-[0.98] font-bold shadow-lg shadow-[#4C1D95]/20 flex items-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  {editingProduct ? 'Update Product' : 'Published Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      {showImageEditor && selectedImageFile && (
        <ImageEditor
          imageFile={selectedImageFile}
          onSave={handleImageSave}
          onCancel={() => { setShowImageEditor(false); setSelectedImageFile(null); }}
        />
      )}
    </div>
  );
}
